import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  concat,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { GraphQLError } from 'graphql';
import { urls } from '@zapier/identity-utils';

import {
  addTruthyParamsToUrl,
  extractParams,
  urlWithNext,
} from '@zapier/url-utils';

function redirectToLogin() {
  if (typeof window !== 'undefined') {
    const reauthUrl = addTruthyParamsToUrl(
      urlWithNext(urls.LOGIN_URL, window.location.pathname),
      {
        reauth: true,
      }
    );
    window.location.href = reauthUrl;
  }
}

function hasRealmMismatchError(
  graphQLErrors: readonly GraphQLError[] | undefined
) {
  return (graphQLErrors || []).some(error => {
    return (
      error.extensions?.code === 403 &&
      error.extensions?.exception?.response?.body?.reason ===
        'invalid_auth_realm'
    );
  });
}

const errorLink = onError(({ graphQLErrors, networkError, ..._rest }) => {
  if (networkError) {
    console.warn(`[Network error]: ${networkError}`);
  }

  if (graphQLErrors) {
    graphQLErrors.map(({ message, locations, path }) => {
      console.warn(`[GraphQL error] ${message}`, {
        message,
        locations,
        path,
      });
    });
  }

  if (
    (networkError &&
      'statusCode' in networkError &&
      networkError.statusCode === 401) ||
    (graphQLErrors && graphQLErrors?.[0]?.extensions?.code === 401)
  ) {
    // If we received a 401 the user needs to log in
    redirectToLogin();
  }

  // Check if there was an auth-realm violation (SAML vs regular credentials)
  if (hasRealmMismatchError(graphQLErrors)) {
    if (typeof window !== 'undefined') {
      const params = extractParams(window.location.href);

      // We only want to dispatch this route if
      // we aren't already in a reauth flow (otherwise we could
      // potentially trigger this multiple times)
      if (!params.reauth) {
        // There was a violation and the user isn't in the auth flow
        // so force the user to reauthenticate
        redirectToLogin();
      }
    }
  }
});

function createApolloClient() {
  // HttpLink to z/z graphql-proxy
  const gqlProxyLink = new HttpLink({
    credentials: 'include',
    uri: '/api/graphql/v2',
  });

  return new ApolloClient({
    cache: new InMemoryCache({
      typePolicies: {
        Flags: {
          merge: true,
        },
      },
    }),
    link: concat(errorLink, gqlProxyLink),
  });
}

let apolloClient: ApolloClient<NormalizedCacheObject> | null = null;

export function getOrCreateApolloClient() {
  // Make sure to create a new client for every server-side request so that data
  // isn't shared between connections (which would be bad)
  if (typeof window === 'undefined') {
    return createApolloClient();
  }

  // Reuse client on the client-side
  if (!apolloClient) {
    apolloClient = createApolloClient();
  }

  return apolloClient;
}
